import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { GuestTopBar } from "../../components/GuestTopBar";
import { Droplets, Flag, Footprints, Mountain, Route, Sunrise, Triangle, Trophy } from "lucide-react";
import { useBetaGate } from "../../hooks/useFeatureFlags";
import { usePageMeta } from "../../hooks/usePageMeta";
import { AchievementBadge } from "../../components/AchievementBadge";
import { CollectionCard } from "../../components/CollectionCard";
import { ProgressRing } from "../../components/ProgressRing";
import { ScoreMeter } from "../../components/ScoreMeter";
import {
  FULL_HISTORY_FEATURES,
  FULL_HISTORY_GBP,
  FULL_HISTORY_PAY_LINE,
  PRO_ANNUAL_NOTE,
  PRO_ANNUAL_PER_MONTH_GBP,
  PRO_ANNUAL_SAVING_PERCENT,
  PRO_ANNUAL_TOTAL_GBP,
  PRO_FEATURES,
  PRO_MONTHLY_GBP,
  PRO_MONTHLY_NOTE,
} from "../../lib/pricing";
import { HERO_LAYER_PARALLAX, mountClimb } from "./climbEngine";
import { LandingMap } from "./LandingMap";
import "./climb.css";

// ── Data ──────────────────────────────────────────────────────────────────────

// Hero terrain, far to near. The lake sits behind the two nearest layers and
// moves with the layer in front of it.
const HERO_LAYERS: { src: string; parallax: number }[] = [
  { src: "/images/landing/wasdale-a.webp", parallax: HERO_LAYER_PARALLAX[0] },
  { src: "/images/landing/wasdale-b.webp", parallax: HERO_LAYER_PARALLAX[1] },
  { src: "/images/landing/wasdale-c.webp", parallax: HERO_LAYER_PARALLAX[2] },
  { src: "/images/landing/wasdale-lake.webp", parallax: HERO_LAYER_PARALLAX[2] },
  { src: "/images/landing/wasdale-d.webp", parallax: HERO_LAYER_PARALLAX[3] },
  { src: "/images/landing/wasdale-e.webp", parallax: HERO_LAYER_PARALLAX[4] },
];

// Strava, Apple Health and Health Connect connect natively; GPX upload is
// always available. Garmin, Komoot and AllTrails work through a GPX export, so
// they are described that way rather than implying a native sync (PLT-6).
const SOURCES: { name: string; color: string }[] = [
  { name: "Strava", color: "var(--coral-400)" },
  { name: "Apple Health", color: "var(--text-secondary)" },
  { name: "Health Connect", color: "var(--text-secondary)" },
  { name: "GPX files", color: "var(--accent)" },
];

// Reference-data counts, not usage numbers: see the derivation in
// packages/db/src/seeds/geographic-data.ts and specs/06-platform.md PLT-6.
const STATS = [
  { value: "2,900+", label: "classified UK peaks" },
  { value: "37", label: "long-distance trails mapped" },
  { value: "65", label: "curated collections" },
  { value: "18", label: "regions with coverage maps" },
];

// Progress is illustrative; the totals are the real collection sizes.
const COLLECTIONS = [
  { title: "Wainwrights", type: "FELLS · LAKE DISTRICT", value: 156, max: 214, icon: <Mountain size={22} /> },
  { title: "Munros", type: "SUMMITS · SCOTLAND", value: 43, max: 282, icon: <Triangle size={22} /> },
  { title: "National Trails", type: "LONG-DISTANCE PATHS", value: 9, max: 16, icon: <Route size={22} /> },
  { title: "Welsh 3000ers", type: "SUMMITS · SNOWDONIA", value: 16, max: 16, icon: <Flag size={22} /> },
];

// Real achievements from packages/db/src/seeds/achievements.ts: names,
// descriptions, tiers and points match the seeded definitions. Unlock state
// and progress are illustrative.
const ACHIEVEMENTS = [
  { title: "Triple Crown", description: "Bag 3 peaks all above 800 m in a single outing.", tier: "silver" as const, points: 50, unlocked: true, icon: <Trophy size={22} /> },
  { title: "Above the Clouds", description: "Summit a peak reaching 1,000 m or higher.", tier: "bronze" as const, points: 25, unlocked: true, icon: <Sunrise size={22} /> },
  { title: "Wainwright Completer", description: "Summit all 214 Wainwright fells in the Lake District.", tier: "platinum" as const, points: 250, unlocked: true, icon: <Mountain size={22} /> },
  { title: "Ultra Distance", description: "Complete a single activity covering 50 km or more.", tier: "gold" as const, points: 100, unlocked: true, icon: <Footprints size={22} /> },
  { title: "Hundred Summits", description: "Summit 100 unique peaks.", tier: "gold" as const, points: 100, unlocked: false, progress: { value: 78, max: 100 }, icon: <Flag size={22} /> },
  { title: "Lake District Explorer", description: "Explore half of the Lake District by area.", tier: "gold" as const, points: 100, unlocked: false, progress: { value: 34, max: 50 }, icon: <Droplets size={22} /> },
];

// Mirrors PlanPage.tsx's plan-gating copy (Pro list is shared from lib/pricing.ts). Wording rule (Strava API Policy
// 5.8): Pro is sold as Atlas's matching, never as access to Strava data, so
// say "matches", not "imports".
const FREE_FEATURES = [
  "Unlimited peak and trail tracking",
  "Strava and health app sync",
  "Matches your last 12 months against every collection",
  "3 GPX uploads a month",
  "Full achievements and Outdoor Score",
  "1 custom list",
  "1 personal route check a month",
];

// Shown only while the beta gate is on (same kill switch as BetaGateModal).
const BETA_FAQ = {
  q: "Atlas is in beta. What does that mean?",
  a: "Everything you see here works today, and creating a free account is open to everyone. What is switched off is payments: during the beta, Pro is unlocked with a beta key instead of a subscription. To get one, create a free account, press any upgrade button and choose Request beta access. If we invite you, we will email a key to the address on your account.",
};
const FAQS = [
  { q: "Does Atlas plan routes or navigate?", a: "No. Your watch and your route apps do that well already. Atlas takes what they record and turns it into collections, coverage and achievements." },
  { q: "How is Atlas different from hill list apps?", a: "Most hill list apps do one thing: you reach a summit and it gets marked off. Atlas does that too, from your Strava or GPX, but summits are only part of the record. It also knows the trig points, bothies, waterfalls and bridges you passed, how much of each National Trail you've walked, which regions you've covered and how much of the map you've actually been across. It counts the second and tenth time up a hill as well as the first, keeps your photos against the places you took them, and turns all of it into a score, achievements and a profile you can share. Atlas doesn't plan routes, navigate or record walks. You keep using whatever you already use for that. Atlas is where the record ends up." },
  { q: "Which sources can I connect?", a: "Strava, Apple Health on iPhone and Health Connect on Android connect directly and sync automatically. Garmin, Komoot and AllTrails do not have a direct connection, so export a GPX file from any of them and import it in seconds. Atlas never asks for another service's password. Every connection is read-only: Atlas never posts, edits or deletes anything in your accounts." },
  { q: "Where do collections come from?", a: "Atlas ships with curated collections: Wainwrights, Munros, Corbetts and the other classic British hill lists, county tops, the National Trails, Scotland's Great Trails and the coast paths, and landmarks such as trig points, bothies and stone circles. You can build your own private lists too." },
  { q: "What is the Outdoor Score?", a: "Your running total for time spent outside. Peaks, landmarks and trail sections award points, covering more of a region adds more, and achievements pay out the most, from Bronze up to Mythic." },
  { q: "What does the free plan include?", a: "Unlimited tracking, achievements, your Outdoor Score, coverage maps, and matching for the last 12 months of your activities, plus 3 GPX uploads a month, 1 custom list and 1 personal route check a month. Older activities are matched with Full history, a one-off purchase. Pro adds unlimited route checks, Insights, Wrapped, unlimited custom lists, 250 Waypoints a month and offline maps on mobile." },
  { q: "What is Full history?", a: `A one-off purchase of £${FULL_HISTORY_GBP}. Atlas matches your full history, not just the last 12 months, for every source, including ones you connect later. It also removes the monthly GPX upload limit. You keep it permanently. Annual Pro includes it; monthly Pro does not.` },
  { q: "Is my data private?", a: "Yes. Your record is private by default. You choose what to share, if anything, and you can delete your account and its data at any time. We do not sell data." },
  { q: "Can I cancel any time?", a: "Any time, from Plan and billing. If you cancel Pro you keep access until the end of the period you have paid for, then drop back to the free plan. Full history stays on after you cancel annual Pro. If you ask for a refund of the Full history purchase, the unlock switches off for new activities and everything already matched stays. Nothing you've collected is ever taken away." },
];



// ── Small pieces ──────────────────────────────────────────────────────────────

/** Fell name and height for a section. On narrow screens it stands in for the pin's pill. */
function Altitude({ peak, metres }: { peak: string; metres: number }) {
  return <div className="cl-alt"><span>{peak}</span><b>{metres} m</b></div>;
}

function Points({ items }: { items: string[] }) {
  return <ul className="cl-points">{items.map((t) => <li key={t}>{t}</li>)}</ul>;
}

/** A real screenshot of the web app, inside a plain browser chrome. */
function BrowserShot({ src, alt, path, children }: { src: string; alt: string; path: string; children?: React.ReactNode }) {
  return (
    <div className="cl-browser">
      <div className="cl-browser-bar" aria-hidden="true"><i /><i /><i /><span>goatlas.co.uk{path}</span></div>
      <img src={src} alt={alt} loading="lazy" width={1920} height={1200} />
      {children}
    </div>
  );
}

const APP_STORE_URL = "https://apps.apple.com/gb/app/atlas-hiking-record/id6788635039";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=co.uk.goatlas.app&hl=en_GB";

/** App screenshot inside the marketing kit's iPhone frame. */
function PhoneShot({ src, alt, className = "" }: { src: string; alt: string; className?: string }) {
  return (
    <div className={`cl-phone ${className}`}>
      <div className="cl-phone-screen"><img src={src} alt={alt} loading="lazy" /></div>
      <img className="cl-phone-frame" src="/images/iphone-frame.png" alt="" loading="lazy" width={806} height={1600} />
    </div>
  );
}

// Seeded, so the summit is the same mountain on every render.
function summitPaths() {
  let a = 5;
  const rnd = () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const n = 64;
  const h: number[] = new Array(n + 1).fill(0);
  h[0] = rnd(); h[n] = rnd();
  for (let step = n, amp = 1; step > 1; step /= 2, amp *= 0.6) {
    for (let i = step / 2; i < n; i += step) h[i] = (h[i - step / 2]! + h[i + step / 2]!) / 2 + (rnd() - 0.5) * amp;
  }
  const lo = Math.min(...h), hi = Math.max(...h);
  const pts = h.map((v, i) => {
    const x = i / n, t = 1 - Math.abs(x - 0.5) * 2, r = (v - lo) / (hi - lo);
    const y = 800 - (Math.pow(t, 1.15) * 640 + r * 170 * (1 - t * 0.85) + (x < 0.5 ? 0 : -60 * (1 - t)) + 60 * t);
    return [x * 1600, Math.max(20, y)] as const;
  });
  pts[n / 2] = [800, 20];
  const line = (p: readonly (readonly [number, number])[]) => p.map(([x, y]) => `L${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  return {
    body: `M0 800 ${line(pts)} L1600 800 Z`,
    lit: `M800 20 ${line(pts.slice(n / 2))} L1600 800 L900 800 L950 640 L860 520 L905 380 L815 250 L850 130 Z`,
  };
}

// ── Guides strip ──────────────────────────────────────────────────────────────

// Shape of the entries in dist/guides-index.json, written by the /guides
// prerender step. The file only exists in a built site; in dev the fetch 404s
// and the section stays hidden.
type GuidePost = { slug: string; title: string; description: string; categoryLabel: string; cover: string | null };

function useGuidesIndex(): GuidePost[] | null {
  const [posts, setPosts] = useState<GuidePost[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/guides-index.json");
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data) && data.length > 0) setPosts(data);
      } catch {
        // dev, or a network hiccup: render nothing
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return posts;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ClimbLandingPage() {
  const rootRef = useRef<HTMLDivElement>(null);
  const betaGate = useBetaGate();
  const guides = useGuidesIndex();
  const [annual, setAnnual] = useState(true);
  const summit = useMemo(summitPaths, []);
  const faqs = betaGate ? [BETA_FAQ, ...FAQS] : FAQS;

  // Same values as index.html's defaults, explicit so SPA navigation back to
  // "/" from a page that overrides meta lands on the canonical marketing copy.
  usePageMeta({
    title: "Atlas - the record of your time outside",
    description:
      "Atlas turns the walks you have already recorded into collections, achievements and coverage maps. The record of your time outside, with a running list of what is left.",
    canonicalPath: "/",
  });

  // The page is a dawn scene, so it is dark whatever theme was last chosen.
  useEffect(() => {
    const el = document.documentElement;
    const previous = el.dataset["theme"];
    el.dataset["theme"] = "dark";
    return () => {
      if (previous) el.dataset["theme"] = previous;
      else delete el.dataset["theme"];
    };
  }, []);

  useEffect(() => {
    if (!rootRef.current) return;
    return mountClimb(rootRef.current);
  }, []);

  return (
    <div className="cl" ref={rootRef}>
      <div className="cl-sky" aria-hidden="true"><div className="cl-dawn" data-cl="dawn" /></div>

      {/* Same header as every other signed-out page (components/GuestTopBar.tsx);
          the landing variant floats it over the hero. */}
      <GuestTopBar variant="landing" ctaLabel="Start your record" />

      <main className="cl-main">
        <div className="cl-journey" data-cl="journey">
          <div className="cl-routes" data-cl="routes" aria-hidden="true" />
          <div className="cl-marker" data-cl="marker" aria-hidden="true" />

          <header className="cl-hero" id="top" data-cl="hero">
            <div className="cl-hero-sky" />
            <div className="cl-hero-glow" />
            {HERO_LAYERS.map((l) => (
              <div className="cl-lay" key={l.src} data-f={l.parallax}><img src={l.src} alt="" /></div>
            ))}
            <div className="cl-hero-shade" />
            <svg className="cl-hov" data-cl="hov" role="img" aria-label="The Wasdale fells with recorded routes and summit pins">
              <path className="cl-hline cl-hline-minor" data-cl="hgab" pathLength={1} />
              <path className="cl-hline" data-cl="hmain" pathLength={1} />
              <g data-cl="hpins" />
            </svg>
            <div className="cl-hero-copy">
              {betaGate && <a className="cl-pill" href="#pricing">In beta. Free to join.</a>}
              <h1><span><b>Every summit,</b></span><span><b>on the record.</b></span></h1>
            </div>
            <div className="cl-hero-sub">
              <p>
                Atlas turns the walks you have already recorded into collections, achievements and coverage maps,
                on the web and on your phone. Atlas does not plan routes. It keeps the record of your time outside,
                and a running list of what is left.
              </p>
              <div className="cl-row">
                <Link className="cl-btn cl-btn-primary" to="/signup">Start your record</Link>
                <a className="cl-btn cl-btn-quiet" href="#how">See how it works</a>
              </div>
              <small>Free to start. No card needed.</small>
            </div>
            <div className="cl-where">Wasdale Head from Wast Water, drawn from elevation data.</div>
          </header>

          <div className="cl-climb">
            <section className="cl-sec cl-sec-centre" id="how" data-route="margin" data-run="left" data-peak="Loughrigg Fell" data-alt="335">
              <div className="cl-head">
                <Altitude peak="Loughrigg Fell" metres={335} />
                <h2>Connect once. Your walks do the rest.</h2>
              </div>
              <ol className="cl-how" data-body data-reveal data-loop>
                <li>
                  <div className="cl-viz cl-viz-sources" aria-hidden="true">
                    {SOURCES.map((s) => <span key={s.name} style={{ "--c": s.color } as React.CSSProperties}>{s.name}</span>)}
                  </div>
                  <div className="cl-how-cap"><span className="cl-how-n">1</span><h3>Connect a source</h3></div>
                  <p>Read-only. Garmin and AllTrails work through GPX.</p>
                </li>
                <li>
                  <div className="cl-viz cl-viz-match" aria-hidden="true">
                    <svg viewBox="0 0 320 200" preserveAspectRatio="xMidYMid slice">
                      <path className="cl-contour" d="M-10 60 C60 20 120 80 190 50 S300 30 340 70" />
                      <path className="cl-contour" d="M-10 100 C70 60 130 120 200 90 S300 70 340 110" />
                      <path className="cl-contour" d="M-10 140 C80 100 140 160 210 130 S300 110 340 150" />
                      <path className="cl-contour" d="M-10 180 C90 140 150 200 220 170 S300 150 340 190" />
                      <path className="cl-track" pathLength={1} d="M20 170 C60 150 70 110 105 96 S150 120 175 78 S230 40 250 58 S285 90 305 44" />
                      {[[105, 96], [175, 78], [250, 58]].map(([x, y]) => (
                        <g className="cl-dot" key={x}><circle cx={x} cy={y} r={13} /><path d={`M${x! - 7} ${y! + 6} l5 -10 l4 6 l3 -4 l4 8 z`} /></g>
                      ))}
                    </svg>
                  </div>
                  <div className="cl-how-cap"><span className="cl-how-n">2</span><h3>Atlas matches your walks</h3></div>
                  <p>Every recorded walk is checked against peaks, trails and landmarks.</p>
                </li>
                <li>
                  <div className="cl-viz cl-viz-ring" aria-hidden="true">
                    <ProgressRing value={156} max={214} size={150} stroke={12} color="gold" showValue={false} />
                    <div className="cl-viz-ring-text"><b>156</b><span>of 214 Wainwrights</span></div>
                  </div>
                  <div className="cl-how-cap"><span className="cl-how-n">3</span><h3>Collect and complete</h3></div>
                  <p>Your lists fill in and you can see what is left.</p>
                </li>
              </ol>
              <div className="cl-stats">
                {STATS.map((s) => <div key={s.label}><b>{s.value}</b><span>{s.label}</span></div>)}
              </div>
            </section>

            <section className="cl-sec cl-sec-centre" id="map" data-route="margin" data-run="right" data-peak="Cat Bells" data-alt="451">
              <div className="cl-head">
                <Altitude peak="Cat Bells" metres={451} />
                <h2>Every walk, drawn on one map.</h2>
                <p>
                  Peaks you have reached turn gold and the routes stay drawn. This is the
                  Exploration Map from the web app, showing one walk over Win Hill and Stanage Edge. Zoom in and
                  press a pin.
                </p>
              </div>
              <div data-body data-reveal style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                <LandingMap />
              </div>
              <p className="cl-note">A real walk in the Peak District, 15 August 2026. Ratings are example data.</p>
            </section>

            <section className="cl-sec" id="collections" data-route="gap" data-peak="Haystacks" data-alt="597">
              <div className="cl-split">
                <div data-reveal>
                  <BrowserShot src="/images/landing/web-collection.webp" path="/collections/wainwrights" alt="The Wainwrights collection in the Atlas web app, 131 of 214 collected" />
                </div>
                <div className="cl-stack">
                  <div className="cl-head">
                    <Altitude peak="Haystacks" metres={597} />
                    <h2>The lists worth finishing, kept for you.</h2>
                    <p>
                      Curated collections built from the established hill lists and National Trail data, filled in from your history.
                      You can build private lists of your own too.
                    </p>
                  </div>
                  <div className="cl-cards">
                    {COLLECTIONS.map((c) => <CollectionCard key={c.title} {...c} />)}
                  </div>
                </div>
              </div>
            </section>

            <section className="cl-sec" id="coverage" data-route="gap" data-peak="The Old Man of Coniston" data-alt="802">
              <div className="cl-split">
                <div className="cl-stack">
                  <div className="cl-head">
                    <Altitude peak="The Old Man of Coniston" metres={802} />
                    <h2>Watch a national park fill in.</h2>
                    <p>
                      Each of the 18 regions has a coverage map showing how much of it you have explored, the
                      peaks, trails and landmarks inside it, and the gaps worth a weekend.
                    </p>
                  </div>
                  <Points items={[
                    "Coverage only ever grows. Nothing you have collected is taken away.",
                    "Closest-to-completion nudges on every plan.",
                    "Set a goal with a date and watch the gap close.",
                  ]} />
                </div>
                <div data-reveal>
                  <BrowserShot src="/images/landing/web-region.webp" path="/regions/lake-district" alt="The Lake District region page in the Atlas web app, 30% complete" />
                </div>
              </div>
            </section>

            <section className="cl-sec" id="score" data-route="gap" data-peak="Blencathra" data-alt="868">
              <div className="cl-split">
                <div className="cl-stack">
                  <div className="cl-head">
                    <Altitude peak="Blencathra" metres={868} />
                    <h2>A score you can only earn outside.</h2>
                    <p>
                      Peaks, landmarks and trail sections add to your Outdoor Score, and achievements pay out
                      more. All of it has to be earned on a real hill. There is nothing for opening the app.
                    </p>
                  </div>
                  <ScoreMeter score={16400} level={41} levelProgress={49} />
                </div>
                <div className="cl-ach" data-reveal>
                  {ACHIEVEMENTS.map((a) => (
                    <AchievementBadge
                      key={a.title} title={a.title} description={a.description} tier={a.tier}
                      points={a.points} unlocked={a.unlocked} icon={a.icon}
                      progress={"progress" in a ? a.progress : null}
                    />
                  ))}
                </div>
              </div>
              <p className="cl-note">Example figures.</p>
            </section>

            <section className="cl-sec" id="apps" data-route="gap" data-peak="Great Gable" data-alt="899">
              <div className="cl-split">
                <div className="cl-stack">
                  <div className="cl-head">
                    <Altitude peak="Great Gable" metres={899} />
                    <h2>On the web, and in your pocket.</h2>
                    <p>
                      The web app puts the whole map and every list on a big screen. The iPhone and Android apps
                      carry the same record up the hill, for checking what is left from the top. Your watch and
                      your other apps still record the walk.
                    </p>
                  </div>
                  <Points items={[
                    "One account, in step across the web and your phone.",
                    "Home-screen widgets that keep the score in view.",
                    "Check in at landmarks while you are stood at them.",
                    "Works without signal, which is most of the places worth going.",
                  ]} />
                  <div className="cl-stores">
                    <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="cl-store cl-store-apple">
                      <img src="/images/store-badges/app-store-badge.svg" alt="Download on the App Store" draggable={false} width={120} height={40} />
                    </a>
                    <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className="cl-store cl-store-google">
                      <img src="/images/store-badges/google-play-badge.png" alt="Get it on Google Play" draggable={false} width={155} height={60} />
                    </a>
                  </div>
                  <p className="cl-note">Free to download.</p>
                </div>
                <div className="cl-devices" data-reveal>
                  <BrowserShot src="/images/landing/web-dashboard.webp" path="/dashboard" alt="The Atlas dashboard in the web app, showing an Outdoor Score of 11,275" />
                  <PhoneShot className="cl-phone-a" src="/images/landing/app-explore-map.webp" alt="The Atlas map in the iPhone app" />
                  <PhoneShot className="cl-phone-b" src="/images/landing/app-widgets.webp" alt="Atlas home-screen widgets on iPhone" />
                </div>
              </div>
            </section>

            <section className="cl-sec cl-sec-centre" id="pricing" data-route="margin" data-run="right" data-peak="Skiddaw" data-alt="930">
              <div className="cl-head">
                <Altitude peak="Skiddaw" metres={930} />
                <h2>Start free. Add your full history when you want it.</h2>
              </div>
              {betaGate && (
                <div className="cl-beta" id="beta">
                  <div>
                    <b>Atlas is in beta, so payments are switched off</b>
                    <p>
                      The free plan is open to everyone today. The prices below are what you will pay at launch,
                      and nothing is charged during the beta. To try Pro now, create a free account, press any
                      upgrade button and choose Request beta access.
                    </p>
                  </div>
                </div>
              )}
              <div className="cl-toggle" role="group" aria-label="Billing period">
                <button type="button" aria-pressed={!annual} onClick={() => setAnnual(false)}>Monthly</button>
                <button type="button" aria-pressed={annual} onClick={() => setAnnual(true)}>Annual, save {PRO_ANNUAL_SAVING_PERCENT}%</button>
              </div>
              <div className="cl-plans cl-plans-3" data-body>
                <div className="cl-plan">
                  <h3>Base camp</h3>
                  <div className="cl-plan-price">Free</div>
                  <p>For getting your history in and seeing your first collections fill.</p>
                  <Link className="cl-btn cl-btn-quiet cl-btn-block" to="/signup">Create free account</Link>
                  <Points items={FREE_FEATURES} />
                </div>
                <div className="cl-plan">
                  <h3>Full history</h3>
                  <div className="cl-plan-price">£{FULL_HISTORY_GBP}<small>one-off</small></div>
                  <p>Pay once and Atlas matches everything you have recorded. {FULL_HISTORY_PAY_LINE}</p>
                  <Link className="cl-btn cl-btn-quiet cl-btn-block" to="/signup">Create account to unlock</Link>
                  <Points items={FULL_HISTORY_FEATURES} />
                </div>
                <div className="cl-plan cl-plan-pro">
                  <h3>Atlas Pro</h3>
                  <div className="cl-plan-price">£{annual ? PRO_ANNUAL_PER_MONTH_GBP : PRO_MONTHLY_GBP}<small>a month</small></div>
                  <p>
                    {annual
                      ? `Billed annually at £${PRO_ANNUAL_TOTAL_GBP}, which is ${PRO_ANNUAL_SAVING_PERCENT}% off the monthly price. ${PRO_ANNUAL_NOTE}`
                      : `Billed monthly. ${PRO_MONTHLY_NOTE} Annual includes it.`}
                  </p>
                  {!annual && (
                    <p className="cl-plan-warn" role="note">
                      {`Monthly does not include Full history: Atlas keeps matching your last 12 months. Choose annual, or add Full history for £${FULL_HISTORY_GBP} once.`}
                    </p>
                  )}
                  <Link className="cl-btn cl-btn-primary cl-btn-block" to="/signup">Upgrade to Pro</Link>
                  <Points items={PRO_FEATURES} />
                </div>
              </div>
              <p className="cl-note">No card needed for the free plan. Cancel Pro whenever you like; Full history stays.</p>
            </section>

            <section className="cl-sec cl-sec-centre" id="faq" data-route="margin" data-run="left" data-peak="Helvellyn" data-alt="950">
              <div className="cl-head">
                <Altitude peak="Helvellyn" metres={950} />
                <h2>Good to know.</h2>
              </div>
              <div className="cl-faq" data-body>
                {faqs.map((f, i) => (
                  <details key={f.q} open={i === 0}>
                    <summary>{f.q}</summary>
                    <p>{f.a}</p>
                  </details>
                ))}
              </div>
              {guides && (
                <>
                  <div className="cl-head"><h2>From the guides</h2></div>
                  <div className="cl-guides">
                    {guides.slice(0, 3).map((p) => (
                      <a className="cl-guide" key={p.slug} href={`/guides/${p.slug}`}>
                        <div className="cl-guide-cover" style={p.cover ? { backgroundImage: `url("${p.cover}")` } : undefined} />
                        <div className="cl-guide-body">
                          <span className="eyebrow">{p.categoryLabel}</span>
                          <h3>{p.title}</h3>
                          <p>{p.description}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </>
              )}
            </section>

            <section className="cl-summit" id="summit" data-peak="Scafell Pike" data-alt="978">
              <svg className="cl-flag" data-cl="flag" viewBox="0 0 60 80" aria-hidden="true">
                <line x1="3" y1="0" x2="3" y2="80" stroke="var(--gold-300)" strokeWidth="4" strokeLinecap="round" />
                <path d="M5 3 L56 16 L5 31 Z" fill="var(--accent)" />
              </svg>
              <svg className="cl-peak" viewBox="0 0 1600 800" preserveAspectRatio="xMidYMin slice" aria-hidden="true">
                <defs>
                  <linearGradient id="cl-peak-body" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="var(--surface-raised)" />
                    <stop offset=".55" stopColor="var(--bg-base)" />
                    <stop offset="1" stopColor="var(--bg-app)" />
                  </linearGradient>
                  <linearGradient id="cl-peak-lit" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="var(--accent)" stopOpacity=".34" />
                    <stop offset=".45" stopColor="var(--accent)" stopOpacity=".07" />
                    <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d={summit.body} fill="url(#cl-peak-body)" />
                <path d={summit.lit} fill="url(#cl-peak-lit)" />
              </svg>
              <div className="cl-summit-cta">
                <small>Scafell Pike, 978 m. The top of England.</small>
                <h2>Start your record.</h2>
                <p>
                  Connect a source and see what you have already done. Free to keep, with Full history at £{FULL_HISTORY_GBP} once
                  and Pro at £{PRO_MONTHLY_GBP} a month or £{PRO_ANNUAL_TOTAL_GBP} a year.
                </p>
                <div className="cl-row" style={{ justifyContent: "center" }}>
                  <Link className="cl-btn cl-btn-primary" to="/signup">Start free</Link>
                  <a className="cl-btn cl-btn-quiet" href="#pricing">See plans</a>
                </div>
              </div>
            </section>
          </div>
        </div>

        <footer className="cl-footer">
          <div className="cl-footer-cols">
            <div>
              <img src="/atlas-logo.svg" alt="Atlas" style={{ height: 26, marginBottom: 18 }} />
              <p style={{ maxWidth: "24em", lineHeight: 1.6 }}>
                A lifetime record of everything you have explored, and a clear view of everything you have not got
                round to yet.
              </p>
            </div>
            <ul>
              <li><h3>Product</h3></li>
              <li><a href="#map">The map</a></li>
              <li><a href="#collections">Collections</a></li>
              <li><Link to="/hills">Hill lists</Link></li>
              <li><a href="#coverage">Coverage maps</a></li>
              <li><a href="#score">Achievements</a></li>
              <li><Link to="/route-analyser">Check a route</Link></li>
              <li><a href="#apps">iOS and Android apps</a></li>
              <li><a href="#pricing">Pricing</a></li>
            </ul>
            <ul>
              <li><h3>Sources</h3></li>
              {["Strava", "Apple Health", "Health Connect", "GPX import", "Garmin, via GPX", "Komoot, via GPX", "AllTrails, via GPX"].map((s) => <li key={s}>{s}</li>)}
            </ul>
            <ul>
              <li><h3>Company</h3></li>
              {/* Plain anchor: /guides is prerendered outside the SPA route tree. */}
              <li><a href="/guides">Blog</a></li>
              <li><Link to="/press">Press and media kit</Link></li>
              <li><a href="mailto:hello@goatlas.co.uk">Contact</a></li>
            </ul>
          </div>
          <div className="cl-footer-base">
            <span>© 2026 Atlas.</span>
            <nav>
              <Link to="/privacy">Privacy</Link>
              <Link to="/terms">Terms</Link>
              <Link to="/community-guidelines">Community guidelines</Link>
              <Link to="/credits">Credits</Link>
            </nav>
          </div>
          {/* ODbL requires the OSM credit (spec 08 LMK-7); the full source list lives on /credits. */}
          <p className="cl-footer-fine">
            Peak data from the{" "}
            <a href="https://www.hills-database.co.uk/" target="_blank" rel="noreferrer">Database of British and Irish Hills</a>{" "}
            (CC BY 4.0). Landmark and map data ©{" "}
            <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap contributors</a>{" "}
            (ODbL), map tiles by <a href="https://openfreemap.org/" target="_blank" rel="noreferrer">OpenFreeMap</a>. National
            Trail routes from Natural England and national park boundaries from the ONS (OGL v3.0); contains OS data ©
            Crown copyright and database right 2022. Hero terrain from Mapzen terrain tiles. Full attribution on
            the <Link to="/credits">Credits</Link> page.
          </p>
        </footer>
      </main>

      <div className="cl-hud" data-cl="hud" aria-hidden="true"><span data-cl="hud-name">Wasdale Head</span><b data-cl="hud-alt">80 m</b></div>
    </div>
  );
}
